import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { Shield, CheckCircle, Phone, Mail, Twitter } from "lucide-react";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const CONTACT_IMG = "https://images.pexels.com/photos/7108324/pexels-photo-7108324.jpeg?auto=compress&cs=tinysrgb&w=700";

export default function Contact() {
  const { t, i18n } = useTranslation();
  const [ref, isVisible] = useScrollAnimation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", treatment: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [ticketId, setTicketId] = useState(null);

  const treatmentOptions = t("contact.treatmentOptions", { returnObjects: true }) || [];
  const trustPoints = t("contact.trustPoints", { returnObjects: true }) || [];

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/consultation`, { ...form, language: i18n.language }, { withCredentials: true });
      setTicketId(res.data.ticket_id);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", treatment: "", message: "" });
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Contact Monimek | Request a Free Medical Consultation</title>
        <meta name="description" content="Contact our medical coordinators today. Get a free, no-obligation treatment plan and price quote from top JCI-accredited hospitals in Turkey." />
        <link rel="canonical" href="https://www.monizmek.com/contact" />
      </Helmet>
      {/* Hero */}
      <section className="page-hero text-white">
        <div className="max-w-[1200px] mx-auto px-6 py-12 text-center">
          <span className="overline-text text-[#3FA7D6]">{t("contact.overline")}</span>
          <h1 className="text-4xl sm:text-5xl font-black text-white font-outfit tracking-tight mt-3 mb-4" data-testid="contact-hero-title">
            {t("contact.title")}
          </h1>
          <p className="text-white/75 text-lg max-w-xl mx-auto">{t("contact.subtitle")}</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-[#F8FAFC]" ref={ref}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className={`grid lg:grid-cols-2 gap-12 anim-init ${isVisible ? "in-view" : ""}`}>
            {/* Form */}
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <h2 className="text-2xl font-bold text-[#0B3C5D] font-outfit mb-6">{t("contact.title")}</h2>

              {status === "success" ? (
                <div
                  className="flex flex-col items-center justify-center py-12 text-center"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-5">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0B3C5D] font-outfit mb-2">Thank You!</h3>
                  <p className="text-[#6B7280] mb-4">{t("contact.form.success")}</p>
                  {ticketId && (
                    <div className="bg-[#F8FAFC] border border-[#3FA7D6]/30 rounded-2xl px-6 py-4 mb-4">
                      <p className="text-xs text-[#6B7280] mb-1">Your Ticket ID</p>
                      <p className="text-2xl font-black text-[#0B3C5D] font-outfit" data-testid="submitted-ticket-id">{ticketId}</p>
                      <p className="text-xs text-[#6B7280] mt-1">Save this ID to track your inquiry</p>
                    </div>
                  )}
                  <a href="/login" className="text-sm font-semibold text-[#3FA7D6] hover:text-[#0B3C5D] transition-colors">
                    Sign in to track your journey →
                  </a>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="consultation-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#0B3C5D] mb-1.5 uppercase tracking-wide">
                        {t("contact.form.name")} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        placeholder={t("contact.form.namePlaceholder")}
                        className="form-input"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B3C5D] mb-1.5 uppercase tracking-wide">
                        {t("contact.form.email")} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        placeholder={t("contact.form.emailPlaceholder")}
                        className="form-input"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-[#0B3C5D] mb-1.5 uppercase tracking-wide">
                        {t("contact.form.phone")} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={form.phone}
                        onChange={handleChange}
                        placeholder={t("contact.form.phonePlaceholder")}
                        className="form-input"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0B3C5D] mb-1.5 uppercase tracking-wide">
                        {t("contact.form.treatment")} *
                      </label>
                      <select
                        name="treatment"
                        required
                        value={form.treatment}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="contact-treatment-select"
                      >
                        <option value="">{t("contact.form.treatmentPlaceholder")}</option>
                        {treatmentOptions.map((opt, i) => (
                          <option key={i} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-[#0B3C5D] mb-1.5 uppercase tracking-wide">
                      {t("contact.form.message")}
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t("contact.form.messagePlaceholder")}
                      className="form-input resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-red-500 text-sm" data-testid="contact-error">{t("contact.form.error")}</p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="w-full btn-primary justify-center py-4 text-base disabled:opacity-70"
                    data-testid="contact-submit-button"
                  >
                    {status === "loading" ? t("contact.form.submitting") : t("contact.form.submit")}
                  </button>
                </form>
              )}
            </div>

            {/* Info Side */}
            <div className="flex flex-col gap-6">
              {/* Image */}
              <div className="rounded-3xl overflow-hidden shadow-[0_10px_40px_rgba(11,60,93,0.10)]" style={{ aspectRatio: "16/9" }}>
                <img src={CONTACT_IMG} alt="Medical clinic" className="w-full h-full object-cover" />
              </div>

              {/* Info card */}
              <div className="bg-white rounded-3xl p-7 border border-gray-100">
                <h3 className="font-bold text-[#0B3C5D] text-lg font-outfit mb-2">{t("contact.infoTitle")}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-5">{t("contact.infoText")}</p>
                <div className="space-y-3">
                  {trustPoints.map((pt, i) => (
                    <div key={i} className="flex items-center gap-3" data-testid={`trust-point-${i}`}>
                      <div className="w-6 h-6 rounded-full bg-[#3FA7D6]/15 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-3.5 h-3.5 text-[#3FA7D6]" />
                      </div>
                      <span className="text-sm text-[#374151]">{pt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Social */}
              <div className="bg-[#0B3C5D] rounded-3xl p-7">
                <h4 className="font-bold text-white mb-4 text-sm">{t("contact.followUs")}</h4>
                <div className="flex gap-3">
                  <a
                    href="https://x.com/monimekllc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-[#3FA7D6] rounded-full text-white text-sm font-medium transition-colors"
                    data-testid="contact-twitter"
                  >
                    <Twitter className="w-4 h-4" /> Twitter/X
                  </a>
                  <a
                    href="https://www.tiktok.com/@monimekllc"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-[#3FA7D6] rounded-full text-white text-sm font-medium transition-colors"
                    data-testid="contact-tiktok"
                  >
                    TikTok
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
